import type {CSSProperties} from "react";
import {useState, useEffect, useRef, useMemo} from "react";
import {maxBy} from "es-toolkit/compat";
import shallowEqual from "shallowequal";
import useWindowSize from "../hooks/useWindowSize";
import type {AnchorAlignment, FitOnScreenResult, OverlayPlacement, OverlayRect} from "./types";

type WindowSize = {width: number; height: number};

type TopHug = "bottom" | "both" | "left";
type LeftHug = "right" | "both" | "left";

function getTopDelta(
  top: number,
  overlayHeight: number,
  viewportMargin: number,
  windowSize: WindowSize
): {hug: TopHug; delta: number} | null {
  const topEdgeOffset = top - (window.pageYOffset || document.body.scrollTop) - viewportMargin;
  const bottomEdgeOffset =
    top + overlayHeight - (window.pageYOffset || document.body.scrollTop) + viewportMargin;

  let hug: TopHug | null = null;
  let delta = 0;
  if (bottomEdgeOffset > windowSize.height) {
    hug = "bottom";
    delta = windowSize.height - bottomEdgeOffset;
  }
  if (topEdgeOffset < 0) {
    hug = hug ? "both" : "left";
    delta = -topEdgeOffset;
  }
  return hug ? {hug, delta} : null;
}

function getLeftDelta(
  left: number,
  overlayWidth: number,
  viewportMargin: number,
  windowSize: WindowSize
): {hug: LeftHug; delta: number} | null {
  const leftEdgeOffset = left - viewportMargin;
  const rightEdgeOffset = left + overlayWidth + viewportMargin;
  let hug: LeftHug | null = null;
  let delta = 0;
  if (rightEdgeOffset > windowSize.width) {
    hug = "right";
    delta = windowSize.width - rightEdgeOffset;
  }
  if (leftEdgeOffset < 0) {
    hug = hug ? "both" : "left";
    delta = -leftEdgeOffset;
  }
  return hug ? {hug, delta} : null;
}

const nextPlacement: Record<OverlayPlacement, OverlayPlacement> = {
  top: "right",
  right: "bottom",
  bottom: "left",
  left: "top",
};

type CalculatedPosition = {
  positionLeft: number | undefined;
  positionRight: number | undefined;
  positionTop: number | undefined;
  positionBottom: number | undefined;
  arrowOffsetLeft: string | undefined;
  arrowOffsetTop: string | undefined;
  placement: OverlayPlacement;
  transformOrigin: string;
  maxWidth: number | undefined;
  maxHeight: number | undefined;
};

function calcOverlayPosition(
  preferredPlacement: OverlayPlacement,
  overlayWidth: number,
  overlayHeight: number,
  anchorRect: OverlayRect,
  windowSize: WindowSize,
  distanceFromAnchor: number,
  forcePlacement: boolean,
  viewportMargin: number,
  anchorAlignment: AnchorAlignment,
  preventPlacement: OverlayPlacement | undefined
): CalculatedPosition {
  if (overlayHeight === undefined) throw new Error("overlayHeight is undefined");
  const scores: {placement: OverlayPlacement; score: number}[] = [];

  let currentPlacement: OverlayPlacement = preferredPlacement;
  let currentScore = 0;
  const anchorHeight = anchorRect.bottom - anchorRect.top;
  const anchorWidth = anchorRect.right - anchorRect.left;

  while (scores.length < 4) {
    if (preventPlacement && currentPlacement === preventPlacement) {
      currentScore = -1;
    } else {
      switch (currentPlacement) {
        case "top":
          currentScore =
            (anchorRect.top - window.pageYOffset - viewportMargin - distanceFromAnchor) /
            overlayHeight;
          break;
        case "right":
          currentScore =
            (windowSize.width - (anchorRect.right + viewportMargin + distanceFromAnchor)) /
            overlayWidth;
          break;
        case "bottom":
          currentScore =
            (windowSize.height +
              window.pageYOffset -
              (anchorRect.bottom + viewportMargin + distanceFromAnchor)) /
            overlayHeight;
          break;
        case "left":
          currentScore = (anchorRect.left - viewportMargin - distanceFromAnchor) / overlayWidth;
          break;
        default:
          throw new Error(`Dunno: ${currentPlacement}`);
      }
    }
    // currentScore > 1 -> has enough space
    // currentScore === 1 -> fit's perfectly, might be due to forced maxWidth though
    if (currentScore > 1 || forcePlacement) break;
    scores.push({placement: currentPlacement, score: currentScore});
    currentPlacement = nextPlacement[currentPlacement];
  }
  if (scores.length === 4) {
    currentPlacement = maxBy(scores, "score")!.placement;
  }

  let positionLeft: number | undefined;
  let positionTop: number | undefined;
  let positionRight: number | undefined;
  let positionBottom: number | undefined;
  let arrowOffsetTop: string | undefined;
  let arrowOffsetLeft: string | undefined;
  let maxWidth: number | undefined;
  let maxHeight: number | undefined;

  const shrinkedHeight = Math.min(windowSize.height - 2 * viewportMargin, overlayHeight);
  const shrinkedWidth = Math.min(windowSize.width - 2 * viewportMargin, overlayWidth);

  if (currentPlacement === "left" || currentPlacement === "right") {
    if (overlayHeight >= windowSize.height - viewportMargin * 2) {
      maxHeight = windowSize.height - viewportMargin * 2;
    }
    positionTop = anchorRect.top + (anchorHeight - shrinkedHeight) / 2;
    const topDelta = getTopDelta(positionTop, shrinkedHeight, viewportMargin, windowSize);
    if (topDelta) {
      if (topDelta.hug === "bottom") {
        positionTop = undefined;
        positionBottom = viewportMargin;
      } else {
        positionTop += topDelta.delta;
      }
    }
    arrowOffsetTop = `${Math.min(
      98,
      Math.max(2, 50 * (1 - (2 * (topDelta ? topDelta.delta : 0)) / shrinkedHeight))
    )}%`;

    if (currentPlacement === "right") {
      positionLeft = anchorRect.right + distanceFromAnchor;
      maxWidth = windowSize.width - (anchorRect.right + viewportMargin + distanceFromAnchor);
    } else {
      // currentPlacement === "left"
      maxWidth = anchorRect.left - viewportMargin - distanceFromAnchor;
      positionRight = windowSize.width - anchorRect.left + distanceFromAnchor;
    }
  } else {
    // currentPlacement === "top" || currentPlacement === "bottom"
    if (overlayWidth >= windowSize.width - viewportMargin * 2) {
      maxWidth = windowSize.width - viewportMargin * 2;
    }
    positionLeft =
      anchorAlignment === "middle"
        ? anchorRect.left + (anchorWidth - shrinkedWidth) / 2
        : anchorAlignment === "left"
          ? anchorRect.left
          : anchorRect.left + (anchorWidth - shrinkedWidth);

    const leftDelta = getLeftDelta(positionLeft, shrinkedWidth, viewportMargin, windowSize);
    if (leftDelta) {
      if (leftDelta.hug === "right") {
        positionLeft = undefined;
        positionRight = viewportMargin;
      } else {
        positionLeft += leftDelta.delta;
      }
    }
    arrowOffsetLeft = `${Math.min(
      98,
      Math.max(2, 50 * (1 - 2 * ((leftDelta ? leftDelta.delta : 0) / shrinkedWidth)))
    )}%`;
    if (currentPlacement === "bottom") {
      positionTop = anchorRect.bottom + distanceFromAnchor;
      maxHeight =
        windowSize.height +
        window.pageYOffset -
        (anchorRect.bottom + viewportMargin + distanceFromAnchor);
    } else {
      maxHeight = anchorRect.top - window.pageYOffset - viewportMargin - distanceFromAnchor;
      positionBottom =
        (document.body.clientHeight || window.innerHeight) - anchorRect.top + distanceFromAnchor;
    }
  }

  let transformOrigin: string;
  switch (currentPlacement) {
    case "top":
      transformOrigin = `${arrowOffsetLeft || 0} 100%`;
      break;
    case "right":
      transformOrigin = `${arrowOffsetLeft || 0} 100%`;
      break;
    case "bottom":
      transformOrigin = `${arrowOffsetLeft || 0} ${distanceFromAnchor}px`;
      break;
    case "left":
      transformOrigin = `${arrowOffsetLeft || 0} 100%`;
      break;
    default:
      throw new Error(`Dunno: ${currentPlacement}`);
  }

  return {
    positionLeft,
    positionRight,
    positionTop,
    positionBottom,
    arrowOffsetLeft,
    arrowOffsetTop,
    placement: currentPlacement,
    transformOrigin,
    maxWidth,
    maxHeight,
  };
}

const useIsSettledIn = () => {
  const [isSettledIn, setIsSettledIn] = useState(false);
  useEffect(() => {
    let id: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      id = null;
      setIsSettledIn(true);
    }, 500);
    return () => {
      if (id) clearTimeout(id);
    };
  }, []);
  return isSettledIn;
};

type RegenotiateArgs = {
  prev: FitOnScreenResult | null;
  next: FitOnScreenResult | null;
  windowSize: WindowSize;
  viewportMargin: number;
  overlayHeight: number;
};

// avoid jumping around if size changes
const regenotiatePosition = ({
  prev,
  next,
  windowSize,
  viewportMargin,
  overlayHeight,
}: RegenotiateArgs): FitOnScreenResult | null => {
  if (prev === next) return prev;
  if (next === null) return next;
  if (!prev) return next;
  if (next.placement === "right" || next.placement === "left") {
    const prevTop = prev.style.top as number | undefined;
    const nextTop = next.style.top as number | undefined;
    if (prevTop === nextTop) return next;
    if (prevTop === undefined || nextTop === undefined) return next;
    const diff = prevTop - nextTop;
    const maxHeight = windowSize.height - 2 * viewportMargin;
    if (prevTop + overlayHeight < maxHeight) {
      return {
        ...prev,
        arrowOffsetTop: `${50 * (1 - (2 * diff) / overlayHeight)}%`,
      };
    }
  }
  return next;
};

type UseFitOnScreenArgs = {
  overlayDims: OverlayRect | null | undefined;
  preferredPlacement?: OverlayPlacement;
  preventPlacement?: OverlayPlacement;
  anchorPosition: OverlayRect | null | undefined;
  forcePlacement?: boolean;
  viewportMargin?: number;
  distanceFromAnchor?: number;
  anchorAlignment?: AnchorAlignment;
};

type PrevState = {
  returnVal: FitOnScreenResult | null;
  anchorPosition: OverlayRect | null;
};

export const useFitOnScreen = ({
  overlayDims,
  preferredPlacement = "top",
  preventPlacement,
  anchorPosition,
  forcePlacement = false,
  viewportMargin = 15,
  distanceFromAnchor = 0,
  anchorAlignment = "middle",
}: UseFitOnScreenArgs): FitOnScreenResult | null => {
  const noDims = !overlayDims;
  const overlayWidth = overlayDims ? overlayDims.width : 1;
  const overlayHeight = overlayDims ? overlayDims.height : 1;
  const windowSize = useWindowSize();
  const isSettledIn = useIsSettledIn();
  const prevRef = useRef<PrevState | undefined>(undefined);
  const next: FitOnScreenResult | null = useMemo(() => {
    if (!anchorPosition) return null;
    if (noDims) {
      const style: CSSProperties = {
        left: viewportMargin,
        top: viewportMargin,
        maxHeight: windowSize.height - 2 * viewportMargin,
        maxWidth: windowSize.width - 2 * viewportMargin,
        zIndex: -1,
      };
      return {
        style,
        arrowOffsetLeft: 0,
        arrowOffsetTop: 0,
        placement: preferredPlacement,
      };
    } else {
      const calculated = calcOverlayPosition(
        (isSettledIn && prevRef.current && prevRef.current.returnVal?.placement) ||
          preferredPlacement,
        overlayWidth,
        overlayHeight,
        anchorPosition,
        windowSize,
        distanceFromAnchor,
        forcePlacement || Boolean(isSettledIn && prevRef.current),
        viewportMargin,
        anchorAlignment,
        preventPlacement
      );
      const style: CSSProperties = {
        left: calculated.positionLeft,
        right: calculated.positionRight,
        top: calculated.positionTop,
        bottom: calculated.positionBottom,
        maxHeight: calculated.maxHeight,
        maxWidth: calculated.maxWidth,
        transformOrigin: calculated.transformOrigin,
        overflow: calculated.maxHeight || calculated.maxWidth ? "auto" : "hidden",
      };
      return {
        style,
        arrowOffsetLeft: calculated.arrowOffsetLeft,
        arrowOffsetTop: calculated.arrowOffsetTop,
        placement: calculated.placement,
      };
    }
  }, [
    preferredPlacement,
    overlayWidth,
    overlayHeight,
    anchorPosition,
    windowSize,
    distanceFromAnchor,
    forcePlacement,
    viewportMargin,
    anchorAlignment,
    noDims,
    isSettledIn,
    preventPlacement,
  ]);

  const returnVal =
    !isSettledIn ||
    !prevRef.current ||
    !shallowEqual(anchorPosition, prevRef.current.anchorPosition)
      ? next
      : regenotiatePosition({
          prev: prevRef.current.returnVal,
          next,
          windowSize,
          viewportMargin,
          overlayHeight,
        });
  useEffect(() => {
    prevRef.current = {returnVal, anchorPosition: anchorPosition ?? null};
  }, [returnVal, anchorPosition]);
  return returnVal;
};
